import { BsCheck } from "@react-icons/all-files/bs/BsCheck";
import { useQueryClient } from "@tanstack/react-query";
import { memo } from "react";
import styled from "styled-components";

import { useCheckCustomTodo } from "@core/hooks/mutations/todo";
import type { CustomTodoItem } from "@core/types/todo";
import queryKeyGenerator from "@core/utils/queryKeyGenerator";

interface Props {
  characterId: number;
  /** 같은 이름의 커스텀 숙제 (캐릭터별). 없으면 null → '-' */
  customTodo: CustomTodoItem | null;
}

/**
 * 커스텀 숙제(주간) 셀.
 * - 캐릭터가 해당 이름의 커스텀 숙제를 가지고 있으면 체크 UI
 * - 없으면 '-'
 */
const CustomCell = ({ characterId, customTodo }: Props) => {
  const queryClient = useQueryClient();
  const checkCustomTodo = useCheckCustomTodo({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyGenerator.getCustomTodos(undefined),
      });
    },
  });

  if (!customTodo) {
    return <Empty>-</Empty>;
  }

  const handleClick = () => {
    checkCustomTodo.mutate({
      characterId,
      customTodoId: customTodo.customTodoId,
    });
  };

  return (
    <Wrapper
      type="button"
      onClick={handleClick}
      $isDone={customTodo.checked}
      aria-label={`커스텀 숙제 ${customTodo.contentName} 토글`}
    >
      <Indicator $isDone={customTodo.checked}>
        {customTodo.checked ? <BsCheck /> : ""}
      </Indicator>
    </Wrapper>
  );
};

export default memo(CustomCell);

const Wrapper = styled.button<{ $isDone: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 44px;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: ${({ $isDone }) => ($isDone ? 0.4 : 1)};

  &:hover {
    background: ${({ theme }) => theme.app.bg.gray1};
  }
`;

const Indicator = styled.div<{ $isDone: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  background: ${({ $isDone, theme }) =>
    $isDone ? theme.app.palette.yellow[300] : "transparent"};
  color: ${({ $isDone, theme }) =>
    $isDone ? theme.app.palette.gray[0] : theme.app.text.dark2};

  svg {
    font-size: 18px;
  }
`;

const Empty = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 44px;
  color: ${({ theme }) => theme.app.text.light2};
  font-size: 13px;
`;
